import React from 'react';
import { useLocale } from "../../../../locales";

export interface ToolbarShortcutOption {
  value: any;
  label: string;
  icon?: React.ReactNode;
}

export interface ToolbarShortcut {
  id: string;
  elementType: string;
  propertyPath: string; // e.g., "style.fontSize", "appearance.layout"
  label: string;
  controlType: 'number' | 'color' | 'select' | 'toggle' | 'slider' | 'segmented';
  options?: ToolbarShortcutOption[];
  min?: number;
  max?: number;
  step?: number;
}

interface ShortcutableControlProps {
  children: React.ReactNode;
  shortcutConfig: Omit<ToolbarShortcut, 'id'>;
  onAddShortcut?: (shortcut: Omit<ToolbarShortcut, 'id'>) => void;
  isInShortcuts?: boolean;
  onRemoveShortcut?: () => void;
}

const LABEL_CLASS_HINTS = [
  'qe-form__label',
  'qe-inline-input__label',
  'qe-color-control__label',
  'qe-properties-section__title-label',
];

const hasLabelClass = (className?: string) =>
  typeof className === 'string' && LABEL_CLASS_HINTS.some((hint) => className.includes(hint));

export const ShortcutableControl: React.FC<ShortcutableControlProps> = ({
  children,
  shortcutConfig,
  onAddShortcut,
  isInShortcuts = false,
  onRemoveShortcut,
}) => {
  const { t } = useLocale();
  const handleToggleShortcut = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isInShortcuts && onRemoveShortcut) {
      onRemoveShortcut();
    } else if (onAddShortcut) {
      onAddShortcut(shortcutConfig);
    }
  };

  const renderPinButton = () => {
    if (!onAddShortcut) {
      return null;
    }

    return (
      <button
        className={`qe-shortcutable-control__pin ${isInShortcuts ? 'qe-shortcutable-control__pin--active' : ''}`}
        type="button"
        onClick={handleToggleShortcut}
        title={isInShortcuts ? t("shortcutableControl.removeFromShortcuts") : t("shortcutableControl.addToToolbar")}
      >
        📌
      </button>
    );
  };

  const enhanceLabelNode = (node: React.ReactElement) => (
    <div className="qe-form__label-wrapper">
      {node}
      {renderPinButton()}
    </div>
  );

  const wrapNodeWithPin = (node: React.ReactNode): { node: React.ReactNode; found: boolean } => {
    if (!React.isValidElement(node)) {
      return { node, found: false };
    }

    const className = (node.props as any)?.className as string | undefined;

    if (hasLabelClass(className)) {
      return { node: enhanceLabelNode(node), found: true };
    }

    const childProps = node.props as any;
    const childNodes = childProps?.children;

    if (childNodes === undefined || childNodes === null) {
      return { node, found: false };
    }

    let foundInChildren = false;
    const wrappedChildren = React.Children.map(childNodes, (child) => {
      const result = wrapNodeWithPin(child);
      if (result.found) {
        foundInChildren = true;
      }
      return result.node;
    });

    if (foundInChildren) {
      return {
        node: React.cloneElement(node, undefined, wrappedChildren),
        found: true,
      };
    }

    return { node, found: false };
  };

  let hasFoundLabel = false;

  const enhancedChildren = React.Children.map(children, (child) => {
    const result = wrapNodeWithPin(child);
    if (result.found) {
      hasFoundLabel = true;
    }
    return result.node;
  });

  // If no label was found, check if parent has label (common pattern: label outside ShortcutableControl)
  if (!hasFoundLabel) {
    if (!onAddShortcut) {
      return <>{children}</>;
    }

    return (
      <div className="qe-shortcutable-control__fallback">
        {children}
        {renderPinButton()}
      </div>
    );
  }

  return <>{enhancedChildren}</>;
};
